import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router'
import ROUTE from '@eu/app/router/appModule/names.js'
import ASSESSMENT_ROUTE from '@shared/modules/assessment/router/names.js'
import useUserStore from '@shared/stores/useUserStore.js'
import useCorporateStore from '@shared/stores/useCorporateStore.js'
import useFeatureFlagStore from '@shared/stores/useFeatureFlagStore'
import useAssessmentStore from '@shared/modules/assessment/stores/useAssessmentStore.js'
import useSelfTestStore from '@shared/modules/assessment/stores/useSelfTestStore.js'
import useRenewalAssessmentStore from '@shared/modules/assessment/stores/useRenewalAssessmentStore'
import useSharedStore from '@shared/stores/useSharedStore.js'
import env from '@shared/env.js'
import i18n from '@shared/localization'
import Tracker from '@shared/Tracker'
import { PAIN_LOCATION_OPTIONS } from '@shared/modules/assessment/config/selfTestOptions'
import { takePendingLpPainLocation } from '@shared/lpPainLocation.js'
import { getOnboardingProgress } from '@eu/app/composables/useOnboardingProgress.js'
import { configureEmbeddedAssessment } from '@eu/app/router/appModule/embeddedAssessment'
import { canReturnToApp } from '@eu/app/helpers/canReturnToApp'
import useDeepLink, {
  DEEP_LINK_CONFIGS,
} from '@shared/composables/useDeepLink.js'
import {
  buildNavigationCtx,
  routeAfterRenewalAssessment,
  routeForCorporate,
  type NavigationBaseCtx,
  type RouteName,
} from '@eu/app/router/appModule/navigation'

// These run after hooks.js and do not re-run for query-only changes; those
// belong in the global hook.

type GuardResult = RouteLocationRaw | undefined

const createBeforeEnterGuard = (
  resolveRedirect: (
    context: NavigationBaseCtx,
    to: RouteLocationNormalized,
  ) => RouteName | undefined,
) => {
  return (to: RouteLocationNormalized): GuardResult => {
    const context = buildNavigationCtx()
    const redirectTarget = resolveRedirect(context, to)

    if (redirectTarget == null) return

    return {
      name: redirectTarget,
      query: to.query,
    }
  }
}

/** DiGA-only routes fall back to the support screen on non-DiGA builds. */
export const requireDigaBuild = createBeforeEnterGuard(({ corporate, diga }) =>
  corporate && !diga ? ROUTE.PRESCRIPTION_SUPPORT : undefined,
)

/** Deep-prescription routes need a subscription — start one via doctor outreach. */
export const requireSubscription = createBeforeEnterGuard(
  ({ corporate, user }) =>
    corporate && !user?.currentSubscription
      ? ROUTE.DOCTOR_OUTREACH_PRIMER
      : undefined,
)

/** Public-insurance-only routes bounce everything else — demos included —
 *  to their verification flow (contrast skipWhenStrictlyPrivateInsurance). */
export const requireStrictlyPublicInsurance = createBeforeEnterGuard(
  ({ corporate }) => {
    if (corporate == null || corporate.insuranceType === 'public') return
    return corporate.isDemo || corporate.isPrivateWithContract
      ? ROUTE.INSURANCE_VERIFICATION_PRIVATE
      : ROUTE.INSURANCE_OVERVIEW
  },
)

/** Private-insurance-only routes bounce public users to the selection. */
export const requirePrivateInsurance = createBeforeEnterGuard(
  ({ corporate }) =>
    corporate?.insuranceType === 'public'
      ? ROUTE.INSURANCE_SELECTION
      : undefined,
)

/** Screens that are meaningless without a corporate — go pick one. */
export const requireCorporate = createBeforeEnterGuard(({ corporate }) =>
  corporate == null ? ROUTE.INSURANCE_SELECTION : undefined,
)

/** Practice confirmation falls back to manual entry without a resolved practice. */
export const requireResolvedPractice = createBeforeEnterGuard(
  ({ hasResolvedPractice }) =>
    hasResolvedPractice ? undefined : ROUTE.DOCTOR_OUTREACH_ADD_PRESCRIBER,
)

/** Users who already provided a phone number skip the phone screen. */
export const skipWhenPhoneProvided = createBeforeEnterGuard(({ user }) =>
  user?.profile?.hasProvidedPhoneNumber ? ROUTE.DOWNLOAD_LINK : undefined,
)
/** The legacy download page swaps to the contextual one in the webOptimizedFunnel. */
// IMPORTANT: the raw flag, NOT isPreSubscriptionWebOptimizedFunnel — that one
// also requires currentSubscription == null, which excludes users who just
// activated a subscription.
export const skipToContextualDownload = createBeforeEnterGuard(
  ({ corporate, flags }) =>
    corporate && flags.webOptimizedFunnel
      ? ROUTE.CONTEXTUAL_DOWNLOAD_LINK
      : undefined,
)

/** Reverse guard: the contextual page outside the funnel. */
export const skipToLegacyDownload = createBeforeEnterGuard(
  ({ corporate, flags }) =>
    corporate && !flags.webOptimizedFunnel ? ROUTE.DOWNLOAD_LINK : undefined,
)

/** HUK corporates verify on their own screen instead of the private one. */
export const skipToHukVerification = createBeforeEnterGuard(({ corporate }) =>
  corporate?.key === 'huk' ? ROUTE.INSURANCE_VERIFICATION_HUK : undefined,
)

/** Strictly-private users skip the legacy code question to their
 *  verification flow; demo corporates intentionally fall through (contrast
 *  requireStrictlyPublicInsurance, which would bounce them). */
export const skipWhenStrictlyPrivateInsurance = createBeforeEnterGuard(
  ({ corporate, flags }) =>
    corporate?.insuranceType === 'private'
      ? routeForCorporate(corporate, flags.webOptimizedFunnel)
      : undefined,
)

/** The primer is a legacy-funnel screen — funnel users skip it. */
export const skipWhenOptimizedFunnel = createBeforeEnterGuard(
  ({ corporate, flags }) =>
    corporate && flags.webOptimizedFunnel
      ? routeForCorporate(corporate, flags.webOptimizedFunnel)
      : undefined,
)

export const resolveSignUpEntry = createBeforeEnterGuard(
  ({ user, corporate, flags }) => {
    if (user === null) return
    if (corporate == null) return ROUTE.INSURANCE_SELECTION
    return routeForCorporate(corporate, flags.webOptimizedFunnel)
  },
)

// --- guards that cannot be pure ctx rules -----------------------------------

/** Renewal entry prepares the assessment's session state before redirecting. */
export const resolveRenewalEntry = (
  to: RouteLocationNormalized,
): GuardResult => {
  const { user, corporate, diga } = buildNavigationCtx()
  if (
    to.query.subscription_renewal !== 'true' ||
    !env.isMskEu ||
    !diga ||
    user?.currentSubscription == null ||
    corporate == null
  ) {
    return
  }

  const initialQuery = useSharedStore().initialQuery as {
    self_test_goal?: string
  }
  useRenewalAssessmentStore().setGoalKey(initialQuery.self_test_goal)
  configureEmbeddedAssessment(
    routeAfterRenewalAssessment(corporate),
    undefined,
    to.query,
  )
  return {
    name: ASSESSMENT_ROUTE.ASSESSMENT_RENEWAL_CHECK_IN,
    query: to.query,
  }
}

/** In-app users without a subscription exit into the app. */
export const returnToApp = async (): Promise<false | undefined> => {
  if (canReturnToApp()) {
    await useDeepLink(DEEP_LINK_CONFIGS.activationProgress).redirect()
    return false
  }
}

/**
 * Stores the pain location from the LP quiz hero like an answer given on the
 * pain location screen, replacing an earlier answer: it only comes from the
 * URL the LP just built (taken at app start, never stored), so it is the
 * visitor's latest answer.
 */
const storeLpPainLocation = (value: unknown): boolean => {
  const selfTestStore = useSelfTestStore()

  const option = PAIN_LOCATION_OPTIONS.find((o) => o.key === value)
  if (!option) return false

  selfTestStore.answer('pain-location', {
    value: option.key,
    text: i18n.global.t(option.label),
    questionText: i18n.global.t('eu__self_test_pain_location_header__web'),
  })
  // A session persisted by an older build restarts the self-test at the first
  // question; its answers are already discarded, so the LP answer starts fresh.
  selfTestStore.needsRestart = false
  // No answer value: analytics only learns that the LP answered the question.
  Tracker.trackKaiaEvent(
    {
      event_name: 'c.lfcycl.prefill_self_test_answer',
      action: 'prefill',
      object_type: 'self_test_answer',
      screen_name: 'onb_pain_locations',
    },
    { answer_key: 'pain-location', source: 'lp' },
  )
  return true
}

export const resolvePreSignupEntry = (
  to: RouteLocationNormalized,
): GuardResult => {
  // Taken before any early return, so a visit that skips the prefill discards
  // the LP answer too.
  const lpPainLocation = takePendingLpPainLocation()

  // authenticated users are handled by resolveSignUpEntry — the check here
  // keeps this guard safe against reordering
  if (useUserStore().user !== null) return

  const featureFlagStore = useFeatureFlagStore()
  if (!featureFlagStore.flags.webOptimizedFunnel) return

  if (!useSelfTestStore().hasCompletedPreSignupAssessment) {
    const endProgress = getOnboardingProgress(ROUTE.INSURANCE_SELECTION)
    if (endProgress == null) {
      throw new Error(
        '[pre-signup-assessment] insurance selection has no onboarding step',
      )
    }
    useAssessmentStore().setEmbeddedFlowContext({
      finishRoute: { name: ROUTE.INSURANCE_SELECTION, query: to.query },
      onboardingProgressRange: { startProgress: 0, endProgress },
    })
    return {
      name: storeLpPainLocation(lpPainLocation)
        ? ASSESSMENT_ROUTE.ASSESSMENT_SELF_TEST_PAIN_INTENSITY
        : ASSESSMENT_ROUTE.ASSESSMENT_SELF_TEST_WORST_PAIN_LOCATION,
      query: to.query,
    }
  }

  // Assessment completion can precede insurance selection.
  if (useCorporateStore().corporate == null) {
    return { name: ROUTE.INSURANCE_SELECTION, query: to.query }
  }
}

/**
 * Authenticated INSURANCE_SELECTION visits skip ahead when the corporate is
 * already resolved into the store — except for backward navigation from the
 * covered screen (user changing insurance). Funnel users route through
 * SIGN_UP, which forwards onward. The unauthenticated ?corporate twin lives
 * in hooks.js: the slug can arrive via a same-route query-only navigation,
 * which never re-runs beforeEnter.
 */
export const skipWhenCorporateKnown = (
  to: RouteLocationNormalized,
  from?: RouteLocationNormalized,
): GuardResult => {
  if (from?.name === ROUTE.INSURANCE_COVERED) return
  if (useUserStore().user === null) return

  const corporate = useCorporateStore().corporate
  if (corporate?.key) {
    const webOptimizedFunnel = useFeatureFlagStore().flags.webOptimizedFunnel
    return {
      name: webOptimizedFunnel
        ? ROUTE.SIGN_UP
        : routeForCorporate(corporate, webOptimizedFunnel),
      query: to.query,
    }
  }
}
